/**
 * Client-side mirror of api/src/lib/reportTemplateEngine.js's
 * `renderTemplate`, for the report-template editor's live preview.
 * Same intentional client/server duplication as messageFormat.ts's
 * `interpolatePreview`/`PREVIEW_TOKENS` (explicitly documented there as
 * a deliberate pattern, not an oversight) — small enough that keeping
 * two copies in sync is cheaper than sharing a package across the API/
 * web boundary.
 *
 * Two differences from the real backend resolver:
 *   - Values here are sample/placeholder data, not a real work order.
 *   - An unresolved token renders as the literal `{{token}}` text
 *     instead of vanishing, so a template author catches a typo before
 *     saving — same reasoning as messageFormat.ts's interpolatePreview.
 */

// A work order/task can have more than one checklist executed against
// it — the preview shows two, same as the real backend now
// concatenates every executed checklist into {{checklist_table}} and
// {{#checklist_item}} (see work-orders.report.js's buildChecklistBlock/
// generateCompletionReport). Only {{checklist.name}}/{{checklist.score}}/
// {{checklist.completion_time}} stay single-checklist (REPORT_PREVIEW_
// TOKENS below, unchanged) — a flat scalar can't represent both.
const SAMPLE_CHECKLIST_TABLE = `
  <div style="margin-bottom:12px">
    <div style="font-size:12.5px;font-weight:700;margin-bottom:4px">Checklist: AHU Daily Inspection</div>
    <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden">
      <thead><tr style="background:#f8fafc">
        <th style="padding:6px 10px;font-size:11px;text-align:left">Item</th>
        <th style="padding:6px 10px;font-size:11px;text-align:left">Response</th>
      </tr></thead>
      <tbody>
        <tr><td style="padding:6px 10px;font-size:12.5px">Is the area clean</td><td style="padding:6px 10px;font-size:12.5px">Yes</td></tr>
        <tr><td style="padding:6px 10px;font-size:12.5px">Any visible damage</td><td style="padding:6px 10px;font-size:12.5px">No</td></tr>
      </tbody>
    </table>
  </div>
  <div>
    <div style="font-size:12.5px;font-weight:700;margin-bottom:4px">Checklist: Fire Safety Audit</div>
    <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden">
      <thead><tr style="background:#f8fafc">
        <th style="padding:6px 10px;font-size:11px;text-align:left">Item</th>
        <th style="padding:6px 10px;font-size:11px;text-align:left">Response</th>
      </tr></thead>
      <tbody>
        <tr><td style="padding:6px 10px;font-size:12.5px">Extinguishers in place</td><td style="padding:6px 10px;font-size:12.5px">Yes</td></tr>
      </tbody>
    </table>
  </div>`

/// Sample data for {{#checklist_item}}...{{/checklist_item}} loops —
/// same answers shown in SAMPLE_CHECKLIST_TABLE above, just as flat
/// per-item records instead of a pre-rendered table. Section labels are
/// prefixed with their own checklist's name, same as the real backend
/// does once a work order has more than one checklist attached.
const SAMPLE_CHECKLIST_ITEMS = [
  { label: 'Is the area clean', value: 'Yes', note: '', flag_label: '', section: 'AHU Daily Inspection — Inspection', media: '' },
  { label: 'Any visible damage', value: 'No', note: 'Note: Checked twice to be sure', flag_label: '', section: 'AHU Daily Inspection — Inspection', media: '' },
  { label: 'Extinguishers in place', value: 'Yes', note: '', flag_label: '', section: 'Fire Safety Audit — Inspection', media: '' },
]

/// Sample data for {{#checklist}}...{{/checklist}} — one entry per
/// sample checklist, each scoped exactly like the real backend's
/// `checklists` context array (work-orders.report.js): its own
/// checklist.name/score/completion_time plus its own checklist_table/
/// checklist_items (unprefixed section labels here, since each is
/// already scoped to one checklist inside the loop).
const SAMPLE_CHECKLISTS: Record<string, string | typeof SAMPLE_CHECKLIST_ITEMS>[] = [
  {
    'checklist.name': 'AHU Daily Inspection',
    'checklist.score': '92%',
    'checklist.completion_time': '18m',
    checklist_table: `
      <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden">
        <thead><tr style="background:#f8fafc">
          <th style="padding:6px 10px;font-size:11px;text-align:left">Item</th>
          <th style="padding:6px 10px;font-size:11px;text-align:left">Response</th>
        </tr></thead>
        <tbody>
          <tr><td style="padding:6px 10px;font-size:12.5px">Is the area clean</td><td style="padding:6px 10px;font-size:12.5px">Yes</td></tr>
          <tr><td style="padding:6px 10px;font-size:12.5px">Any visible damage</td><td style="padding:6px 10px;font-size:12.5px">No</td></tr>
        </tbody>
      </table>`,
    checklist_items: [
      { label: 'Is the area clean', value: 'Yes', note: '', flag_label: '', section: 'Inspection', media: '' },
      { label: 'Any visible damage', value: 'No', note: 'Note: Checked twice to be sure', flag_label: '', section: 'Inspection', media: '' },
    ],
  },
  {
    'checklist.name': 'Fire Safety Audit',
    'checklist.score': '',
    'checklist.completion_time': '6m',
    checklist_table: `
      <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden">
        <thead><tr style="background:#f8fafc">
          <th style="padding:6px 10px;font-size:11px;text-align:left">Item</th>
          <th style="padding:6px 10px;font-size:11px;text-align:left">Response</th>
        </tr></thead>
        <tbody>
          <tr><td style="padding:6px 10px;font-size:12.5px">Extinguishers in place</td><td style="padding:6px 10px;font-size:12.5px">Yes</td></tr>
        </tbody>
      </table>`,
    checklist_items: [
      { label: 'Extinguishers in place', value: 'Yes', note: '', flag_label: '', section: 'Inspection', media: '' },
    ],
  },
]

const SAMPLE_PARTS_TABLE = `
  <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden">
    <thead><tr style="background:#f8fafc"><th style="padding:6px 10px;font-size:11px;text-align:left">Part</th><th style="padding:6px 10px;font-size:11px;text-align:right">Cost</th></tr></thead>
    <tbody><tr><td style="padding:6px 10px;font-size:12.5px">Air filter</td><td style="padding:6px 10px;font-size:12.5px;text-align:right">$24.00</td></tr></tbody>
  </table>`

const SAMPLE_COMMENTS_LIST = `
  <div style="border:1px solid #e2e8f0;border-radius:8px;padding:9px 14px">
    <div style="font-size:12.5px;font-weight:600">Priya Sharma</div>
    <div style="font-size:12.5px;color:#475569">Checked and replaced the part.</div>
  </div>`

const SAMPLE_SIGNATURES_BLOCK = `
  <div style="border:1px solid #e2e8f0;border-radius:8px;padding:10px 14px;display:inline-block">
    <div style="font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase">Closed by</div>
    <div style="font-size:13px;font-weight:700">Priya Sharma</div>
  </div>`

const SAMPLE_ATTACHMENTS_LIST = `
  <div style="border:1px solid #e2e8f0;border-radius:8px;padding:10px 14px;font-size:12px;color:#64748b">photo-before.jpg, photo-after.jpg</div>`

const SAMPLE_TIMELINE_BLOCK = `
  <div>
    <div style="display:flex;align-items:stretch">
      <div style="width:24px;flex-shrink:0;display:flex;flex-direction:column;align-items:center">
        <div style="width:10px;height:10px;border-radius:50%;background:#3B82F6;margin-top:4px;flex-shrink:0"></div>
        <div style="width:2px;flex:1;background:#e2e8f0;margin:3px 0"></div>
      </div>
      <div style="padding:0 0 10px 8px">
        <div style="font-size:13px;font-weight:600;color:#1e293b">Created</div>
        <div style="font-size:12px;color:#64748b">Sep 10, 2026, 09:15 AM · by Anjali Mehta</div>
      </div>
    </div>
    <div style="display:flex;align-items:stretch">
      <div style="width:24px;flex-shrink:0;display:flex;flex-direction:column;align-items:center">
        <div style="width:10px;height:10px;border-radius:50%;background:#059669;margin-top:4px;flex-shrink:0"></div>
      </div>
      <div style="padding:0 0 10px 8px">
        <div style="font-size:13px;font-weight:600;color:#1e293b">Acknowledged & Resolved</div>
        <div style="font-size:12px;color:#64748b">Sep 10, 2026, 02:05 PM · by Anjali Mehta</div>
      </div>
    </div>
  </div>`

export const REPORT_PREVIEW_TOKENS: Record<string, string> = {
  'wo.code': 'WO-00123',
  'wo.title': 'Replace HVAC filter on Unit A-101',
  'wo.description': 'Filter was visibly clogged during routine inspection.',
  'wo.status': 'Closed',
  'wo.priority': 'High',
  'wo.due_date': 'Sep 12, 2026, 05:00 PM',
  'wo.work_type': 'Preventive',
  'wo.category': 'HVAC',
  'wo.kind_label': 'Work Order',

  'site.name': 'Ramanathapuram RAM Branch',
  'site.address': '12 MG Road, Ramanathapuram',
  'site.entity': 'RAM',
  'site.state': 'Tamil Nadu',
  'site.category': 'Branch',
  'site.field1': 'Sample value',
  'site.field2': 'Sample value',
  'site.field3': 'Sample value',
  'site.field4': 'Sample value',
  'site.field5': 'Sample value',
  'site.branch_area_sqft': '2400',

  'location.name': '2nd Floor Server Room',

  'asset.name': 'AHU Unit A-101',
  'asset.code': 'AST-00456',

  'assigned.name': 'Ravi Kumar',
  'reporter.name': 'Anjali Mehta',
  'created_by.name': 'Anjali Mehta',

  'checklist.name': 'AHU Daily Inspection',
  'checklist.score': '92%',
  'checklist.completion_time': '18m',

  'dates.created_at': 'Sep 10, 2026, 09:15 AM',
  'dates.closed_at': 'Sep 10, 2026, 11:40 AM',
  'dates.resolved_at': 'Sep 10, 2026, 02:05 PM',
  'dates.total_duration': '2h 25m',

  'org.currency_symbol': '$',

  checklist_table: SAMPLE_CHECKLIST_TABLE,
  parts_table: SAMPLE_PARTS_TABLE,
  comments_list: SAMPLE_COMMENTS_LIST,
  signatures_block: SAMPLE_SIGNATURES_BLOCK,
  attachments_list: SAMPLE_ATTACHMENTS_LIST,
  timeline_block: SAMPLE_TIMELINE_BLOCK,
}

const TOKEN_RE = /\{\{([\w.]+)\}\}/g

// Mirrors reportTemplateEngine.js's row-hiding logic — the one narrow
// exception to "flat substitution, no conditionals". Since the sample
// data below has a value for every real token, a hide-if-empty row
// only ever disappears in this preview when its token name is a typo
// (unrecognized key) — same failure mode as a scalar typo, just a
// missing row instead of a literal `{{typo}}`.
const TR_OPEN_RE = /<tr\b[^>]*>/gi
const ROW_BOUNDARY_RE = /<table\b|<\/table\s*>|<\/tr\s*>/gi

/**
 * Finds each hide-if-empty row's *own* closing </tr> by tracking table
 * nesting depth from the row's start, rather than grabbing the next
 * </tr> in the string — a row can legitimately contain a fully nested
 * <table> of its own (e.g. a labeled section's own heading bar), and a
 * naive search would stop at that inner table's close, leaving the
 * row's trailing tags orphaned and corrupting everything parsed after.
 * See reportTemplateEngine.js's stripHiddenRows for the full story —
 * this is the same function, kept in sync by hand per this file's
 * documented client/server duplication convention.
 */
// `lookup` resolves a token's value against either the top-level
// REPORT_PREVIEW_TOKENS (the default, used outside any {{#checklist}}
// loop) or a merged per-checklist context (inside one iteration of the
// loop, where checklist.name/score/completion_time must resolve to
// THAT checklist's own values, not the top-level sample). Mirrors
// reportTemplateEngine.js's `context` object parameter — this file
// just keeps it as a function instead of a plain object since the
// top-level sample data lives in a separately-typed constant.
type TokenLookup = (key: string) => string | undefined

function defaultLookup(key: string): string | undefined {
  return REPORT_PREVIEW_TOKENS[key]
}

function stripHiddenRows(html: string, lookup: TokenLookup): string {
  let result = ''
  let pos = 0
  while (true) {
    TR_OPEN_RE.lastIndex = pos
    const openMatch = TR_OPEN_RE.exec(html)
    if (!openMatch) return result + html.slice(pos)

    const hideAttr = openMatch[0].match(/\bdata-hz-hide-if-empty="([\w.]+)"/)
    if (!hideAttr) {
      result += html.slice(pos, openMatch.index + openMatch[0].length)
      pos = openMatch.index + openMatch[0].length
      continue
    }

    ROW_BOUNDARY_RE.lastIndex = openMatch.index + openMatch[0].length
    let depth = 0
    let rowEnd = -1
    let boundary: RegExpExecArray | null
    while ((boundary = ROW_BOUNDARY_RE.exec(html))) {
      const tag = boundary[0].toLowerCase()
      if (tag.startsWith('<table')) depth += 1
      else if (tag.startsWith('</table')) depth -= 1
      else if (depth === 0) { rowEnd = ROW_BOUNDARY_RE.lastIndex; break }
    }
    if (rowEnd === -1) return result + html.slice(pos) // unclosed row — bail out safely

    const value = lookup(hideAttr[1] ?? '')
    const shouldHide = value === undefined || value === ''
    result += html.slice(pos, shouldHide ? openMatch.index : rowEnd)
    pos = rowEnd
  }
}

// Mirrors reportTemplateEngine.js's expandChecklistItemLoop — the
// second (and so far only other) narrow exception to "flat
// substitution, no conditionals". See that file's doc comment for the
// full reasoning; this is the same construct, sample data instead of a
// real work order's.
const CHECKLIST_ITEM_LOOP_RE = /\{\{#checklist_item\}\}([\s\S]*?)\{\{\/checklist_item\}\}/g
const ITEM_FIELD_RE = /\{\{item\.(\w+)\}\}/g

function expandChecklistItemLoop(html: string, items: typeof SAMPLE_CHECKLIST_ITEMS): string {
  return html.replace(CHECKLIST_ITEM_LOOP_RE, (_, rowTemplate: string) => {
    return items.map((item) => rowTemplate.replace(ITEM_FIELD_RE, (_m, field: string) => {
      const value = (item as Record<string, string>)[field]
      return value ?? ''
    })).join('')
  })
}

function renderSection(html: string, lookup: TokenLookup, items: typeof SAMPLE_CHECKLIST_ITEMS): string {
  const withRowsResolved = stripHiddenRows(html, lookup)
  const withItemsExpanded = expandChecklistItemLoop(withRowsResolved, items)
  return withItemsExpanded.replace(TOKEN_RE, (match, key: string) => lookup(key) ?? match)
}

// Mirrors reportTemplateEngine.js's expandChecklistLoop — the third
// exception, for a template that wants its own heading + table PER
// checklist instead of the merged checklist_table/checklist_item
// above. Everything between {{#checklist}} and {{/checklist}} is
// rendered once per SAMPLE_CHECKLISTS entry, with checklist.*/
// checklist_table/checklist_items resolving to THAT checklist's own
// values (falling back to the top-level sample for every other token,
// e.g. {{wo.title}}, used freely inside the loop too).
const CHECKLIST_LOOP_RE = /\{\{#checklist\}\}([\s\S]*?)\{\{\/checklist\}\}/g

function expandChecklistLoop(html: string): string {
  return html.replace(CHECKLIST_LOOP_RE, (_, sectionTemplate: string) =>
    SAMPLE_CHECKLISTS.map((cl) => {
      const lookup: TokenLookup = (key) => {
        const own = cl[key]
        if (typeof own === 'string') return own
        return REPORT_PREVIEW_TOKENS[key]
      }
      const items = Array.isArray(cl.checklist_items) ? cl.checklist_items : SAMPLE_CHECKLIST_ITEMS
      return renderSection(sectionTemplate, lookup, items)
    }).join(''))
}

export function renderTemplatePreview(html: string): string {
  const withChecklistsExpanded = expandChecklistLoop(String(html ?? ''))
  return renderSection(withChecklistsExpanded, defaultLookup, SAMPLE_CHECKLIST_ITEMS)
}

/**
 * The attribute `instrumentSourcePositions` stamps onto every opening
 * tag, holding `"{start}:{end}"` — character offsets of that tag *in
 * the original, un-instrumented source string* (the exact text the
 * CodeMirror editor holds). Used by the editor's click-to-inspect
 * feature to map a clicked preview element back to its source range.
 */
export const SOURCE_POS_ATTR = 'data-hz-src'

const OPENING_TAG_RE = /<([a-zA-Z][\w-]*)((?:\s+[^<>]*?)?)(\/?)>/g

/**
 * Best-effort HTML "instrumentation" pass: stamps every opening tag
 * with a `data-hz-src="start:end"` attribute pointing back at its own
 * position in `source`. Deliberately a flat regex scan, not a real
 * parser — a comment that happens to contain a literal `<tag>`-looking
 * substring could mis-tag (none of the templates this app generates or
 * seeds do that), and it doesn't understand nesting. That's an
 * acceptable tradeoff: this output only ever feeds the editor's
 * preview iframe (never saved, never sent to the backend — the real
 * `html_content` stays exactly what the user typed), so a rare
 * mis-mapped click is a minor annoyance, not a correctness bug.
 *
 * Must run on the raw, unresolved source (before renderTemplatePreview
 * substitutes tokens) so the recorded offsets line up with what
 * CodeMirror shows — token substitution changes string length, which
 * would otherwise throw the offsets off.
 */
export function instrumentSourcePositions(source: string): string {
  let out = ''
  let last = 0
  OPENING_TAG_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = OPENING_TAG_RE.exec(source))) {
    const start = m.index
    const end = start + m[0].length
    out += source.slice(last, start)
    const selfClosing = m[3] === '/'
    const tagOpen = m[0].slice(0, m[0].length - (selfClosing ? 2 : 1))
    out += `${tagOpen} ${SOURCE_POS_ATTR}="${start}:${end}"${selfClosing ? '/' : ''}>`
    last = end
  }
  out += source.slice(last)
  return out
}

/// Grouped for the editor's variable picker — insertion order here is
/// display order in that panel.
export const REPORT_VARIABLE_GROUPS: { label: string; tokens: string[] }[] = [
  { label: 'Work order', tokens: ['wo.code', 'wo.title', 'wo.description', 'wo.status', 'wo.priority', 'wo.due_date', 'wo.work_type', 'wo.category', 'wo.kind_label'] },
  { label: 'Site', tokens: ['site.name', 'site.address', 'site.entity', 'site.state', 'site.category', 'site.field1', 'site.field2', 'site.field3', 'site.field4', 'site.field5', 'site.branch_area_sqft'] },
  { label: 'Location & asset', tokens: ['location.name', 'asset.name', 'asset.code'] },
  { label: 'People', tokens: ['assigned.name', 'reporter.name', 'created_by.name'] },
  { label: 'Checklist', tokens: ['checklist.name', 'checklist.score', 'checklist.completion_time'] },
  { label: 'Dates', tokens: ['dates.created_at', 'dates.closed_at', 'dates.resolved_at', 'dates.total_duration'] },
  { label: 'Organization', tokens: ['org.currency_symbol'] },
  { label: 'Blocks (place once, each expands to a pre-built section)', tokens: ['checklist_table', 'parts_table', 'comments_list', 'signatures_block', 'attachments_list', 'timeline_block'] },
]
